/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { IDynamicFormBlur, IDynamicFormError, IDynamicFormEvent, IDynamicFormState, IDynamicFormValue } from "../DynamicFormContracts";

export function copyPropsAndFillWithValue<T, V>(obj: T, value: V): { [k in keyof T]: V } {
  const out = {} as { [k in keyof T]: V };
  for (let key in obj) {
    out[key] = value;
  }
  return out;
}

export function anyTrue<T>(error: IDynamicFormError<T> | IDynamicFormBlur<T>): boolean {
  for (let key in error) {
    if (error[key]) return true;
  }
  return false;
}

function handleFormEvent<T>(
  evt: IDynamicFormEvent<keyof T, any>,
  { value, blur, error }: IDynamicFormState<T>,
): IDynamicFormState<T> {
  if (!evt.target) throw new Error('Invalid event target')
  const { name, value: fieldValue } = evt.target;
  if (!name) throw new Error('Invalid field name')
  if (evt.type === "blur") blur = { ...blur, [name]: true };
  else if (evt.type === "change") value = { ...value, [name]: fieldValue };
  else if (evt.type === "error") error = { ...error, [name]: evt.target!.value };
  else console.warn(`El tipo de evento ${evt.type} no es manejado por Handle Form Event`);
  const newState = { value, blur, error };
  return newState;
}

function createFormState<T>(
  initValues?: T,
): IDynamicFormState<T> {
  const value = { ...initValues } as IDynamicFormValue<T>;
  const error = copyPropsAndFillWithValue(value, false);
  const blur = copyPropsAndFillWithValue(value, false);
  return { value, error, blur };
}

export interface IUseFormProps<T> {
  initValues?: T;
  onSubmit?: (value: IDynamicFormValue<T>, anyError: boolean, error: IDynamicFormError<T>) => void;
  onChange?: (state: IDynamicFormState<T>) => void;
  disabled?: boolean;
  reset?: boolean;
};

export default function useDynamicForm<T>({
  initValues,
  onSubmit,
  onChange,
  disabled,
  reset,
}: IUseFormProps<T>) {

  const [formState, setFormState] = useState(createFormState(initValues));

  useEffect(() => {
    if (reset) setFormState(() => createFormState(initValues));
  }, [reset]);

  useEffect(() => {
    if (onChange) onChange(formState);
  }, [formState]);

  const revalidate = () => {
    setFormState(pv => ({ ...pv, value: { ...initValues } as IDynamicFormValue<T> }));
  };

  const firstInitValues = useMemo(() => initValues, []);

  useEffect(() => {
    if (firstInitValues !== initValues) revalidate();
  }, [initValues]);

  const handleEvent = (evt: IDynamicFormEvent<keyof T, any>) => {
    setFormState(oldState => handleFormEvent(evt, oldState));
  };

  const handleSubmit = () => {
    const newState = { ...formState, blur: copyPropsAndFillWithValue(formState.blur, true) as IDynamicFormBlur<T> };
    setFormState(oldState => ({ ...oldState, blur: copyPropsAndFillWithValue(oldState.blur, true) }));
    if (onSubmit) {
      onSubmit(newState.value, anyTrue(newState.error), newState.error);
    }
  }

  const inputProps = (name: keyof T) => {
    return {
      name,
      onChange: handleEvent,
      onBlur: handleEvent,
      onError: handleEvent,
      error: (formState.error[name] === true) && (formState.blur[name] === true),
      value: formState.value[name] as any,
      disabled
    };
  };

  return {
    handleEvent,
    handleSubmit,
    state: formState,
    anyError: anyTrue(formState.error),
    revalidate,
    inputProps,
  };
}
